// Modules
import React, { useRef } from "react";
// @ts-ignore
import sessionStorage from "store/storages/sessionStorage";
import moment from "moment";
// @ts-ignore
import { LocalizationContext } from "react-localize";

// Styles
import "./book-my-holiday.sass";

// Services
import ReservationService from "Services/reservation.service";
import LocalizationService from "Services/localization.service";
import { handle401Error } from "Services/service.utils";

// Interfaces
import ResortResult from "Interfaces/ResortResultInterface";
import { AvailableResult } from "Interfaces/ResortResultInterface";
import SearchCriteria from "Interfaces/SearchCriteriaInterface";
import FilterResult from "Interfaces/FilterResultInterface";
import FormData from "Interfaces/FormDataInterface";
import Results from "Interfaces/ResultsInterface";
import EligibilityResults from "Interfaces/EligibilityResultsInterface";
import SelectedFilters from "Interfaces/SelectedFiltersInterface";
import Item from "Interfaces/ItemInterface";
import { HoldRequestData } from "Interfaces/HoldRequestData";
import { WaitlistData } from "Interfaces/WaitlistData";
import { RequestError } from "Interfaces/RequestError";
import { ErrorModal } from "Interfaces/ErrorModalInterface";
import ContractType from "Interfaces/AdvanceSearchInterface";
import { OwnerContract, OwnerShipDetails } from "Interfaces/OwnerInterface";
import { ContractToolTip } from "Interfaces/ContractToolTipInterface";
import { EnhancedSegment } from "Interfaces/Segment";

// Components
import OmniPresentSearchForm from "Components/PWAS/Components/OmniPresentSearchBar/OmniPresentSearchBar";
import SearchResults from "Components/PWAS/Components/SearchResults/SearchResults";
import MvLoader from "Generic/MvLoader/MvLoader";
import MvError from "Generic/MvError/MvError";
import CheckAvailability from "Components/PWAS/Components/CheckAvailability/CheckAvailability";
import HeldReservations from "Components/PWAS/Components/HeldReservation/HeldReservations";
import ConfirmAvailability from "Components/PWAS/Components/CheckAvailability/ConfirmAvailability";
import MvModal, { MvMessageModal, MvErrorModal } from "Generic/MvModal";
import MvContractToolTip from "Generic/ToolTip/MvContractToolTip";
import { Position } from "Generic/ToolTip/ToolTip";

// Constants
import {
  ALERT_MODALS,
  ALERT_MODAL_TYPES,
  RequestStatus,
  ScreenType,
} from "Constants";
import { InitErrorModal } from "Constants/InitInterfaces";

// Other
import log, { EXEC_MODE } from "cfg";
import { MvPointsCalculatorModal } from "Generic/MvModal/MvPointsCalculatorModal";
import CalculatorFormData from "Interfaces/CalculatorFormDataInterface";
import { ResortPointInterface } from "Interfaces/ResortPointInterface";
import { ReactAppMode } from "Interfaces/ReactAppModes";
import { getUniqueContractSegments } from "Utils/Search";
import { wait } from "@testing-library/dom";
import { logAnalyticData, trackAnalyticData } from "Utils/analytics";
import { cleanupLastReservationSession } from "Utils/CleanupReservationSession";

const md5 = require("md5");

type AvailabilityState = {
  loadError: boolean;
  resultsError: boolean;
  errorMessage: string;
  searchResultsErrorMessage: string;
  searchResults: ResortResult[];
  filters: FilterResult;
  showResults: boolean;
  searchCriteria: SearchCriteria;
  form: FormData;
  calculatorForm: CalculatorFormData;
  showSearch: boolean;
  showAvailability: boolean;
  isDateChanged: boolean;
  showConfirmAvailability: boolean;
  hideLoader: boolean;
  selectedFilters: SelectedFilters;
  availability: any;
  heldResorts: ResortResult[];
  heldResortsError: boolean;
  heldResortsErrorMessage: string;
  heldResortsShowLoader: boolean;
  displayHeldResorts: boolean;
  availableResort?: ResortResult;
  alertModal: ErrorModal;
  showPointsCalculatorModal: boolean;
  calculatorRequestStatus: RequestStatus;
  calculatorRequestData: ResortPointInterface[];
  selectedCalculatorFilter: CalculatorFormData;
  contractToolTips: ContractToolTip[];
  selectedMonth: any;
  waitlistToggleChecked: boolean;
  editingSegment: EnhancedSegment | null;
  changeLoaderMessage: boolean;
  isAlternateSearchAvailable: boolean;
  constructionMsgData: string | null;
};
type Props = {
  token: string;
  lang: string;
  setScreenType(
    screenType: ScreenType,
    aypResId?: number,
    waitlistId?: number
  ): void;
  setEditingSegment(segment: EnhancedSegment): void;
  editingSegment: EnhancedSegment | null;
  resortLimit?: string;
  apiTimeout: string;
  pointsSearch?: string;
};

const InitSelectedFilters: SelectedFilters = {
  roomType: {
    filters: [],
    expanded: false,
  },
  views: {
    filters: [],
    expanded: false,
  },
  activity: {
    filters: [],
    expanded: false,
  },
  area: {
    filters: [],
    expanded: false,
  },
};

const initialCalculatorForm: CalculatorFormData = {
  numNights: 0,
  numGuests: 0,
  startDate: "",
  resorts: [],
  segments: [],
  ownerBenefitLevel: "",
};

const InitState: AvailabilityState = {
  loadError: false,
  resultsError: false,
  errorMessage: "",
  searchResultsErrorMessage: "",
  heldResorts: [],
  heldResortsError: false,
  heldResortsErrorMessage: "",
  heldResortsShowLoader: false,
  displayHeldResorts: true,
  searchCriteria: {
    sessionId: "",
    searchMonths: [],
    activities: [],
    accessibility: [],
    resorts: [],
    contracts: [],
    segments: [],
    ownerBenefitLevel: "",
    enableOwnerSearchAvailability: false,
  },
  isDateChanged: false,
  form: {
    flexibleSearch: true,
    activity: "",
    numNights: 0,
    numGuests: 0,
    accessibility: "",
    startDate: "",
    resorts: [],
    contracts: new Array<string>(),
    advancedSearch: false,
    hearing: false,
    sortBy: "",
    contractDetails: [],
    contractTypes: [],
  },
  calculatorForm: {
    numNights: 0,
    numGuests: 0,
    startDate: "",
    resorts: [],
    segments: [],
    ownerBenefitLevel: "",
  },
  searchResults: [],
  filters: {
    roomType: [],
    views: [],
    activities: [],
    area: [],
  },
  showResults: false,
  showSearch: false,
  showAvailability: false,
  showConfirmAvailability: false,
  hideLoader: true,
  selectedFilters: Object.assign({}, InitSelectedFilters),
  availability: {
    form: null,
    result: null,
  },
  availableResort: undefined,
  alertModal: Object.assign(InitErrorModal, {}),
  showPointsCalculatorModal: false,
  calculatorRequestStatus: RequestStatus.LOADED,
  calculatorRequestData: [],
  selectedCalculatorFilter: {
    numNights: 0,
    numGuests: 0,
    startDate: "",
    resorts: [],
    segments: [],
    ownerBenefitLevel: "",
  },
  waitlistToggleChecked: false,
  contractToolTips: [],
  selectedMonth: null,
  editingSegment: null,
  constructionMsgData: null,
  changeLoaderMessage: false,
  isAlternateSearchAvailable: false,
};

const initialForm: FormData = {
  flexibleSearch: true,
  activity: "",
  numNights: 0,
  numGuests: 0,
  accessibility: "",
  startDate: "",
  resorts: [],
  contracts: new Array<string>(),
  advancedSearch: false,
  hearing: false,
  sortBy: "",
  contractDetails: [],
  contractTypes: [],
};

class BookMyHoliday extends React.Component<Props, AvailabilityState> {
  reservationService: ReservationService;
  localizationService: LocalizationService;
  _isMounted: boolean;

  constructor(prop: Props) {
    super(prop);
    this.showAvailabilitySection = this.showAvailabilitySection.bind(this);
    this.hideAvailabilitySection = this.hideAvailabilitySection.bind(this);
    this.arrangeSortResults = this.arrangeSortResults.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.clearSearch = this.clearSearch.bind(this);
    this.removeResorts = this.removeResorts.bind(this);
    this.removeItem = this.removeItem.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
    this.onAdvancedSearch = this.onAdvancedSearch.bind(this);
    this.toggleFilterGroup = this.toggleFilterGroup.bind(this);
    this.onFilterSelect = this.onFilterSelect.bind(this);
    this.deleteHeldResorts = this.deleteHeldResorts.bind(this);
    this.getHeldResorts = this.getHeldResorts.bind(this);
    this.placeHold = this.placeHold.bind(this);
    this.createWaitlist = this.createWaitlist.bind(this);
    this.toggleAlertModal = this.toggleAlertModal.bind(this);
    this.togglePointsCalculatorModal =
      this.togglePointsCalculatorModal.bind(this);
    this.goToAYP = this.goToAYP.bind(this);
    this.goToWaitlistAYP = this.goToWaitlistAYP.bind(this);
    this.confirmHeldResort = this.confirmHeldResort.bind(this);
    this.onMonthSelect = this.onMonthSelect.bind(this);
    this.reservationService = new ReservationService();
    this.localizationService = new LocalizationService();
    this.state = Object.assign({}, InitState);
    this._isMounted = false;
  }

  isFormDefault = () => {
    const {
      flexibleSearch,
      activity,
      numNights,
      numGuests,
      accessibility,
      startDate,
      resorts,
      advancedSearch,
      hearing,
      sortBy,
    } = this.state.form;

    if (
      flexibleSearch === true &&
      activity === "" &&
      !(numNights > 0) &&
      !(numGuests > 0) &&
      accessibility === "" &&
      startDate === "" &&
      resorts.length === 0 &&
      advancedSearch === false &&
      hearing === false &&
      sortBy === ""
    ) {
      return true;
    }

    return false;
  };

  /**
   * Given an array of resort property codes,
   * remove those resorts from the array of selected resorts.
   *
   * @param {string[]} the property codes to remove
   */
  removeResorts(removeResorts: string[]): void {
    let selected = this.state["form"]["resorts"];
    let newValue = selected.filter((selectedResort: string) => {
      return removeResorts.indexOf(selectedResort) === -1;
    });

    this.handleInput("resorts", newValue);
  }

  removeCalculatorResorts = (removeResorts: string[]): void => {
    let selected = this.state["calculatorForm"]["resorts"];
    let newValue = selected.filter((selectedResort: string) => {
      return removeResorts.indexOf(selectedResort) === -1;
    });

    this.handleCalculatorInput("resorts", newValue);
  };

  componentDidMount() {
    const { localize } = this.context;
    this._isMounted = true;
    
    if (sessionStorage.read(ReservationService.BOOKED_RESERVATION) !== null) {
      cleanupLastReservationSession();
    }

    let dataLoading: [Promise<SearchCriteria>, Promise<ResortResult[]>] = [
      this.reservationService.getSearchCriteria(),
      this.reservationService.getHeldResorts(),
    ];

    Promise.all(dataLoading)
      .then((results: any[]) => {
        let scStr: string = JSON.stringify(results[0]);
        let search: SearchCriteria = JSON.parse(scStr);
        log.debug(`Search criteria: ${JSON.stringify(search)}`);

        const selectedAvailabilityState = JSON.parse(
          sessionStorage.read(ReservationService.SELECT_AVAILABILITY_STATE)
        );
        if (selectedAvailabilityState) {
          this.setState(
            {
              ...this.state,
              availability: {
                result: selectedAvailabilityState.availabilityResult,
                form: selectedAvailabilityState.availabilityForm,
              },
              selectedFilters: selectedAvailabilityState.selectedFilters,
              selectedMonth: selectedAvailabilityState.selectedMonth,
            },
            () => {
              //no need to do anything here. this is just to force setState to update synchronously so the change is reflected before the check availability component renders
            }
          );
        }

        let state: AvailabilityState = { ...this.state };
        state.heldResorts = results[1];
        state.searchCriteria = search;
        state.form.advancedSearch = !search.enableOwnerSearchAvailability;
        state.showSearch = true;
        state.editingSegment = this.props.editingSegment;
        const formState = sessionStorage.read(
          ReservationService.OPS_FORM_SESSION_KEY
        );
        if (formState != undefined) {
          state.form = JSON.parse(formState);
        } else {
          state.form.contracts = search.contracts
            .filter((x: any) => x.isSearchAvailabilityValidContract)
            .map((conType) => conType.contracts)
            .flat()
            .map((con) => con.id);
          state.form.contractDetails = search.contracts
            .filter((x: any) => x.isSearchAvailabilityValidContract)
            .map((conType) => conType.contracts)
            .flat()
            .map((con) => con);
        }

        //add full list of contracts to fromState so that it's stored in session
        state.form.contractTypes = search.contracts;

        //fetch tooltips to pass to advanced search component
        this.fetchContactTooltips(state.searchCriteria.contracts);

        //HERE JESSICA we manually add RESET element on top of accessibility and activity drop-down lists for search criteria
        const resetItem: Item = {
          value: "RESET",
          description: localize("None"),
        };

        let indOfReset = search.accessibility
          ? search.accessibility.findIndex((item) => item.value === "RESET")
          : 0;

        if (indOfReset < 0) {
          search.accessibility.unshift(resetItem);
        }
        indOfReset = search.activities
          ? search.activities.findIndex((item) => item.value === "RESET")
          : 0;
        if (indOfReset < 0) {
          search.activities.unshift(resetItem);
        }
        if (this._isMounted) {
          this.setState(state);
        }
      })
      .catch((error: any) => {
        // const errMsg = error.message ? `${error.message}` : "Unspecified error";
        const errMsg = error.message
          ? `${error.message}`
          : localize("static.content.pwas-search-fatal-error-message");
        if (!handle401Error(error)) {
          if (this._isMounted) {
            this.setState({
              showSearch: true,
              loadError: true,
              errorMessage: errMsg,
            });
          }
          log.error(`Error: ${JSON.stringify(error)}`);
        }
      });
    this.cleanUpConstructionMessages();
    // add click listener to clear session on logout
    const logoutBtn = document.getElementById("dt-logout");
    if (logoutBtn) {
      logoutBtn.addEventListener("click", () => {
        this.clearSession("ALL");
      });
    }
  }

  clearSession = (key: string) => {
    if (key === "ALL") {
      sessionStorage.remove(ReservationService.SEARCH_CRITERIA_SESSION_ID);
      sessionStorage.remove(ReservationService.SEGMENTS);
      sessionStorage.remove(ReservationService.BENEFIT_LEVEL);
      sessionStorage.remove(ReservationService.OPS_FORM_SESSION_KEY);
      sessionStorage.remove(ReservationService.SELECT_AVAILABILITY_STATE);
      sessionStorage.remove(ReservationService.APPLIED_POINTS);
      sessionStorage.remove(ReservationService.CONTRACT_TOOL_TIPS);
      sessionStorage.remove(ReservationService.SELECTED_PREFERENCES);
      sessionStorage.remove(ReservationService.BOOKED_RESERVATION);
      sessionStorage.remove(ReservationService.USAGE_REQUIRED_BY_DAY);
      sessionStorage.remove(ReservationService.ALTERNATE_SEARCH_RESORT);
    } else {
      sessionStorage.remove(key);
    }
  };
  componentWillUnmount(): void {
    this._isMounted = false;
  }

  clearSearch() {
    let newForm = Object.assign({}, initialForm);

    //store all contract types on form
    newForm.contractTypes = this.state.searchCriteria.contracts;

    //default selection is to select all contracts
    newForm.contracts = this.state.searchCriteria.contracts
      .filter((x: any) => x.isSearchAvailabilityValidContract)
      .map((conType) => conType.contracts)
      .flat()
      .map((con) => con.id);

    newForm.contractDetails = this.state.searchCriteria.contracts
      .filter((x: any) => x.isSearchAvailabilityValidContract)
      .map((conType) => conType.contracts)
      .flat()
      .map((con) => con);

    this.setState({
      form: newForm,
      heldResorts: [],
      searchResults: [],
      availability: { form: null, result: null },
      selectedMonth: null,
    });

    this.clearFilters();
    sessionStorage.remove(ReservationService.OPS_FORM_SESSION_KEY);
    sessionStorage.remove(ReservationService.SELECT_AVAILABILITY_STATE);
    sessionStorage.remove(ReservationService.ALTERNATE_SEARCH_RESORT);
    let formInState = JSON.parse(JSON.stringify(this.state.form));
    /**
     * had to remove this property from the deep copy of the form from state before caching, because we are caching based on the md5 conversion of the form from state
     * the page was exhibiting unusual behavior, since advanced search property in state can change and cache will not be invalidated, which will lead to more bugs
     */
    delete formInState.advancedSearch;
    sessionStorage.remove(md5(JSON.stringify(formInState)));
  }

  clearCalculatorSearch = () => {
    let newForm = Object.assign({}, initialCalculatorForm);

    this.setState({
      calculatorForm: newForm,
    });
  };

  /**
   * attempt to place a hold on a property
   */
  placeHold(holdRequestData: HoldRequestData) {
    return new Promise((resolve, reject) => {
      // This is the date format that the backend wants
      holdRequestData.checkIn = moment(holdRequestData.checkIn).format(
        "YYYYMMDD"
      );
      console.log({ holdRequestData });
      this.setState({ heldResortsShowLoader: true });
      this.reservationService
        .placeHold(holdRequestData)
        .then((holdResult) => {
          this.getHeldResorts(false);
          resolve(holdResult);
        })
        .catch((error: RequestError) => {
          const { errorCode } = error;
          const alertModalType: string = ALERT_MODAL_TYPES[errorCode];
          if (alertModalType !== undefined) {
            this.toggleAlertModal(alertModalType);
          }
          this.setState({ heldResortsShowLoader: false });
          reject(error);
        });
    });
  }

  createWaitlist(waitlistData: WaitlistData) {
    return new Promise((resolve, reject) => {
      this.reservationService
        .createWaitlist(waitlistData)
        .then((waitlistResult) => {
          //this.goToWaitlistAYP();
          resolve(waitlistResult);
        })
        .catch((error: RequestError) => {
          const { errorCode } = error;
          console.log("ERROR CODE HERE");
        });
    });
  }

  /**
   * navigate to the Apply Your Points jsp
   */
  goToAYP(resId: number): void {
    let selectedAvailabilityState = {
      availabilityResult: this.state.availability.result,
      availabilityForm: this.state.availability.form,
      selectedFilters: this.state.selectedFilters,
      selectedMonth: this.state.selectedMonth,
    };

    sessionStorage.write(
      ReservationService.SELECT_AVAILABILITY_STATE,
      JSON.stringify(selectedAvailabilityState)
    );
    sessionStorage.write(
      ReservationService.ALTERNATE_SEARCH_RESORT,
      this.state.isAlternateSearchAvailable
    );
    if ([ReactAppMode.VOA, ReactAppMode.LOCAL].includes(EXEC_MODE)) {
      this.props.setScreenType(ScreenType.APPLY_POINTS, resId);
    } else {
      // VCAP (production)
      window.location.href = `/applyPoints.jsp?resId=${resId}`;
    }
  }

  /**
   * navigate to the Apply Your Points jsp
   */
  goToWaitlistAYP(waitlistId: number): void {
    let selectedAvailabilityState = {
      availabilityResult: this.state.availability.result,
      availabilityForm: this.state.availability.form,
      selectedFilters: this.state.selectedFilters,
    };

    window.history.pushState(selectedAvailabilityState, "");
    sessionStorage.write(
      ReservationService.SELECT_AVAILABILITY_STATE,
      JSON.stringify(selectedAvailabilityState)
    );

    if ([ReactAppMode.VOA, ReactAppMode.LOCAL].includes(EXEC_MODE)) {
      this.props.setScreenType(ScreenType.APPLY_POINTS, -1, waitlistId);
    } else {
      // VCAP (production)
      window.location.href = `/applyPoints.jsp?waitListId=${waitlistId}`;
    }
  }

  /**
   * toggle an alert modal with header and message
   */
  toggleAlertModal(type?: string) {
    let header,
      message = "";

    if (type && ALERT_MODALS[type] !== undefined) {
      header = ALERT_MODALS[type].header;
      message = ALERT_MODALS[type].message;
    }

    this.setState({
      alertModal: {
        show: !this.state.alertModal.show,
        header,
        message,
      },
    } as any);
  }

  /**
   * toggle an points calculator modal
   */
  togglePointsCalculatorModal() {
    this.setState({
      showPointsCalculatorModal: !this.state.showPointsCalculatorModal,
      calculatorRequestData: [],
    } as any);
  }

  /**
   * When a search is performed, we want to retain the filters that
   * were previously selected by the user. However, upon getting new search
   * results, if those filters are no longer applicable to the results,
   * remove those filters from the selected filters.
   */
  stripInApplicableFilters(filters: FilterResult) {
    const selectedFilters = { ...this.state.selectedFilters };
    // For each filter in selected filters, check if that filter exists
    // in the array of filters passed in params
    // If it doesn't exist, remove it from the selected filters
    for (const filterType in selectedFilters) {
      const selectedFilter: any = { ...selectedFilters[filterType] };
      let filtersOfType = [...selectedFilter.filters].filter((filter) => {
        return filters[filterType].some(
          (filterResult) => filterResult.value === filter
        );
      });

      selectedFilter.filters = filtersOfType;
      selectedFilters[filterType] = selectedFilter;
    }

    return selectedFilters;
  }
  cleanUpConstructionMessages() {
  const resultList = document.querySelector('.result-list-wrapper');
  if (!resultList) return;

  // Get all construction messages and result li's
  const constructionLis = resultList.querySelectorAll('li.construction-data.notif-li');
  const resultLis = resultList.querySelectorAll('li.single-results[data-id]:not(.construction-data)');
  
  // Track which IDs have matches
  const matchedMessageIds = new Set<string>();
  const matchedResultIds = new Set<string>();
  
  // Find matching pairs
  constructionLis.forEach(constructionLi => {
    const msgId = constructionLi.getAttribute('data-message');
    if (!msgId) return;
    
    // Check if this message has a matching result li
    const hasMatch = Array.from(resultLis).some(
      function (resultLi) { 
        var id = resultLi.getAttribute('data-id');
        if (!id) return false;
        return id.toLowerCase() === msgId.toLowerCase();
      }
    );
    
    if (hasMatch) {
      matchedMessageIds.add(msgId.toLowerCase());
    }
  });
  
  // Also find matches from result side
  resultLis.forEach(resultLi => {
    const resultId = resultLi.getAttribute('data-id');
    if (!resultId) return;
    
    const hasMatch = Array.from(constructionLis).some(
      function (constructionLi) {
        const msgId = constructionLi.getAttribute('data-message');
        if (!msgId) return false;
        return msgId.toLowerCase() === resultId.toLowerCase();
      }
    );
    
    if (hasMatch) {
      matchedResultIds.add(resultId.toLowerCase());
    }
  });
   console.log('Matched construction message IDs:', matchedMessageIds);
   console.log('Matched result IDs:', matchedResultIds);
  // Remove UNMATCHED construction messages (orphans)
  constructionLis.forEach(li => {
    const msgId = li.getAttribute('data-message');
    if (!msgId || !matchedMessageIds.has(msgId.toLowerCase())) {
      console.log('Removing unmatched construction message:', msgId);
      li.remove();
    } else {
      console.log('Keeping matched construction message:', msgId);
    }
  });
  
  // Remove UNMATCHED result li's (orphans)
  // resultLis.forEach(li => {
  //   const resultId = li.getAttribute('data-id');
  //   if (!resultId || !matchedResultIds.has(resultId.toLowerCase())) {
  //     console.log('Removing unmatched result li:', resultId);
  //     li.remove();
  //   } else {
  //     console.log('Keeping matched result li:', resultId);
  //   }
  // });
}


  handleConstructionMsg = () => {
    console.log("Handling construction message in parent");
    this.cleanUpConstructionMessages();
    this.setState({
      constructionMsgData: this.state.form.startDate,
    });
  };

  async onSubmit() {
    const { localize } = this.context;
    // Clear session stoage related to prev booking (if any)
    cleanupLastReservationSession();
    
    const segments = getUniqueContractSegments(this.state.form);
    this.handleConstructionMsg();
    /**
     * we are caching the default search response in the sesssion storage for the first time, default search is invoked when the page loads.
     * This is done because, we do not want to make the default search API call again, when the user clicks on the back to search button from the calendar.
     * This is done to improve performance
     * TTL: 15 mins in prod, 10 seconds in dev
     */
    let formInState = JSON.parse(JSON.stringify(this.state.form));
    /**
     * had to remove this property from the deep copy of the form from state before caching, because we are caching based on the md5 conversion of the form from state
     * the page was exhibiting unusual behavior, since advanced search property in state can change and cache will not be invalidated, which will lead to more bugs
     */
    delete formInState.advancedSearch;
    
    let newSearchHash = md5(JSON.stringify(formInState));
    // remove old cache
    const oldSearchHash = sessionStorage.read("LAST_SEARCH_HASH");
    if (oldSearchHash && oldSearchHash !== newSearchHash) {
      sessionStorage.remove(oldSearchHash);
    }
    let defaultSearchFromSession = sessionStorage.read(oldSearchHash);

    if (defaultSearchFromSession) {
      this.setState(
        {
          hideLoader: false,
          availability: { form: Object.assign({}, this.state.form) },
        },
        () => {
          let defaultSearchResults: Results = JSON.parse(
            defaultSearchFromSession
          );
          let state: AvailabilityState = this.state;
          if (state.showAvailability && state.availability) {
            let newAvailability = this.state.availability;
            newAvailability.result = null;
            state.showAvailability = false;
            state.hideLoader = true;
            state.availability = newAvailability;
            state.selectedMonth = null;
          }
          state.filters = defaultSearchResults.filters;
          state.selectedFilters = this.stripInApplicableFilters(
            defaultSearchResults.filters
          );
          state.searchResults = defaultSearchResults.results;
          state.showResults = true;
          state.hideLoader = true;
          state.loadError = false;
          state.resultsError = false;
          this.setState(state);
        }
      );
    }
    else if (!defaultSearchFromSession && (formInState.numNights === null || formInState.numGuests === null || !formInState.resorts)) {
      this.clearSearch();
      this.setState({
        hideLoader: true,
      });
    } 
    else {
      let results: Results;
      this.setState({
        hideLoader: false,
        //changeLoaderMessage:false,
        isAlternateSearchAvailable: false,
      });
      try {
        results = await this.reservationService.search(
          this.state.form,
          segments
        );
        console.log("==>>>> results.results", results.results);

        const selectedResortId = this.state.form.resorts;
        const hasEmptyAvailability =
          results &&
          results.results.every(
            (r) =>
              r &&
              selectedResortId.includes(r.propertyCode) &&
              (!r.available || r.available.length === 0)
          );
        if (hasEmptyAvailability) {
          const selectedSet = new Set(selectedResortId);
          const siblings: any = [];
          let allCovered = false;
          const selectedStates = new Set<string>();
          let unnamedStateCounter = 0;

          // determine which states contain the selected resorts and whether all resorts in that state are selected
          this.state["searchCriteria"].resorts.forEach((country: any) => {
            country.states.forEach((state: any) => {
              const ids = state.resorts.map((r: any) => r.resortId);
              if (ids.some((id: string) => selectedSet.has(id))) {
                const rawName = String(state.name || "").trim();
                const stateKey =
                  rawName.length > 0
                    ? rawName
                    : `unnamed_state_${++unnamedStateCounter}`;
                selectedStates.add(stateKey);
              }
              if (ids.every((id: any) => selectedSet.has(id))) allCovered = true;
            });
          });

          // Group specific countries as one alternate group when needed
          /*
          const groupedCountryNames = [
            "aruba",
            "bahamas",
            "st. kitts & nevis",
            "u.s. virgin islands",
          ];
          */

          // Populate groupedCountryNames from searchCriteria by detecting "caribbean" in country names.
          // Deduplicate results so we get a stable list of matching country name tokens.
          const countriesList = this.state["searchCriteria"] && this.state["searchCriteria"].resorts
            ? this.state["searchCriteria"].resorts
            : [];
            const groupedCountryNames = Array.from(
            new Set(
              countriesList
              .filter((c: any) => {
                const continent = String(c.continent || "").toLowerCase();
                return (
                continent.includes("caribbean")
                );
              })
              .map((c: any) => String(c.name || "").toLowerCase())
              .filter((name: string) => name.length > 0)
            )
            );

          console.log("groupedCountryNames", groupedCountryNames, 'countriesList', countriesList);

          // collect resorts for those matching countries (case-insensitive match)
          const groupIds: string[] = [];
          this.state["searchCriteria"].resorts.forEach((country: any) => {
            const countryName = String(country.name || "").toLowerCase();
            if (groupedCountryNames.some(g => countryName.includes(g))) {
              country.states.forEach((state: any) => {
                (state.resorts || []).forEach((r: any) => {
                  if (r && r.resortId) groupIds.push(r.resortId);
                });
              });
            }
          });
          const groupSet = new Set(groupIds);
                  
          // Booleans for grouped (Caribbean) vs non-group selection
           const anySelectedInGroup = Array.from(selectedSet).some((id) =>
             groupSet.has(id)
           );
           const anySelectedNotInGroup = Array.from(selectedSet).some((id) =>
             !groupSet.has(id)
           );
           const selectionHasMixedGroup = anySelectedInGroup && anySelectedNotInGroup;
           const allGroupResortsSelected =
             groupIds.length > 0 && groupIds.every((id) => selectedSet.has(id));

            // if multiple states selected OR all resorts in a state are selected,
            // and either none of the selected resorts belong to the grouped countries
            // OR ALL grouped-country resorts are selected, then skip sibling search
            // Skip sibling search when:
            //  - selection spans multiple states OR the state is fully selected
            // AND one of:
            //  - no selected resort belongs to the grouped pool
            //  - the grouped pool exists and is fully selected
            //  - the selection is a mix of grouped-country resorts AND non-grouped resorts
            console.log("selectedStates", selectedStates.size > 1, allCovered, anySelectedInGroup, allGroupResortsSelected, selectionHasMixedGroup);
            if (
              (selectedStates.size > 1 || allCovered) &&
              (
                !anySelectedInGroup ||
                allGroupResortsSelected ||
                selectionHasMixedGroup
              )
            ) {
            console.warn(
              "Multiple states selected OR no resorts available in selected state. Skipping sibling search retry."
            );
            } else {
            console.warn(
              `Empty availability found, retrying search for alternate resorts`
            );
            this.setState({
              changeLoaderMessage: true,
              isAlternateSearchAvailable: true,
            });
            const alternateSearchLimit = Number(process.env.REACT_APP_ALTERNATE_SEARCH_LIMIT) || 15;
            console.log("alternateSearchLimit",alternateSearchLimit);

            // If any selected resort belongs to the grouped countries, use all grouped-country resorts as the sibling pool.
            const selectedInGroup = Array.from(selectedSet).some(id => groupSet.has(id));

            if (selectedInGroup && groupIds.length > 0) {
              const candidates = groupIds.filter(id => !selectedSet.has(id));
              const shuffled = candidates.sort(() => Math.random() - 0.5);
              for (const id of shuffled) {
                if (siblings.length >= alternateSearchLimit) break;
                if (!siblings.includes(id)) siblings.push(id);
              }
            } else {
              // Default: only search siblings within the same state(s)
              this.state["searchCriteria"].resorts.forEach((country: any) => {
                country.states.forEach((state: any) => {
                  const ids = (state.resorts || []).map((r: any) => r.resortId);
                  if (ids.some((id: string) => selectedSet.has(id))) {
                    const shuffledIds = [...ids].sort(() => Math.random() - 0.5);
                    for (const id of shuffledIds) {
                      if (siblings.length >= alternateSearchLimit) break;
                      if (!selectedSet.has(id) && !siblings.includes(id)) siblings.push(id);
                    }
                  }
                });
              });
            }
             console.log(siblings);
             console.log("alternate search limit::",Number(process.env.REACT_APP_ALTERNATE_SEARCH_LIMIT));
             const siblingResults = await this.reservationService.search(
               { ...this.state.form, resorts: [...siblings] },
               segments
             );
             results = {
               ...results,
               results: [...results.results, ...siblingResults.results],
             };
           }
         }

        this.setState({
          hideLoader: false,
          changeLoaderMessage: false,
          // isAlternateSearchAvailable:true,
        });

        let state: AvailabilityState = this.state;
        state.filters = results.filters;
        state.selectedFilters = this.stripInApplicableFilters(results.filters);
        state.searchResults = results.results;
        state.showResults = true;
        state.hideLoader = true;
        state.loadError = false;
        state.resultsError = false;
        state.isDateChanged = false;
        state.changeLoaderMessage = false;
        state.availability = { form: Object.assign({}, this.state.form) };
        state.selectedMonth = state.form.startDate;
        let formInState = JSON.parse(JSON.stringify(this.state.form));
        /**
         * had to remove this property from the deep copy of the form from state before caching, because we are caching based on the md5 conversion of the form from state
         * the page was exhibiting unusual behavior, since advanced search property in state can change and cache will not be invalidated, which will lead to more bugs
         */
        delete formInState.advancedSearch;

        let newSearchHash = md5(JSON.stringify(formInState));
        sessionStorage.write(newSearchHash, JSON.stringify(results));        
        // save latest hash
        sessionStorage.write("LAST_SEARCH_HASH", newSearchHash);

        this.setState(state, () => {
          const currentHash = newSearchHash;

          setTimeout(() => {
            // let form = JSON.parse(JSON.stringify(this.state.form));
            // /**
            //  * had to remove this property from the deep copy of the form from state before caching, because we are caching based on the md5 conversion of the form from state
            //  * the page was exhibiting unusual behavior, since advanced search property in state can change and cache will not be invalidated, which will lead to more bugs
            //  */
            // delete form.advancedSearch;
            // sessionStorage.remove(md5(JSON.stringify(form)));
            // console.log("==> Key removed fter 15 min: ", md5(JSON.stringify(form)));
            const latestHash = sessionStorage.read("LAST_SEARCH_HASH");

            // only cleanup if this search is still the latest one
            if (latestHash === currentHash) {
              sessionStorage.remove(currentHash); 
              sessionStorage.remove("LAST_SEARCH_HASH");

              console.log("==> Key removed after 15 min:", currentHash);
            }
          }, 900000);
        });
      } catch (error: any) {
        console.log('error search', error);
        if(error.code === 404 && error.message !== null) {
          let state: AvailabilityState = this.state;
          state.hideLoader = true;
          state.resultsError = true;
          state.searchResultsErrorMessage = error.message && error.errorCode === "ROOMS_NOT_FOUND"
            ? localize("static.content.pwas-search-room-capacity-error-message")
            : localize("static.content.pwas-search-fatal-error-message");
          this.setState(state);
          log.error(`Error: ${JSON.stringify(error)}`);
        } else if (!handle401Error(error)) {
          let state: AvailabilityState = this.state;
          state.hideLoader = true;
          state.resultsError = true;
          // state.searchResultsErrorMessage = error.message ? `${error.message}` : "Unspecified error";
          state.searchResultsErrorMessage = error.message
            ? `${error.message}`
            : localize("static.content.pwas-search-fatal-error-message");
          this.setState(state);
          log.error(`Error: ${JSON.stringify(error)}`);
        }
      }
      //   this.setState({
      //     hideLoader: false,
      //     changeLoaderMessage: false,
      //     availability: { form: Object.assign({}, this.state.form) },
      //   });
      sessionStorage.write(
        ReservationService.OPS_FORM_SESSION_KEY,
        JSON.stringify(this.state.form)
      );
    }
  }

  onCalculatorSubmit = (selecteddata: CalculatorFormData) => {
    this.setState({ calculatorRequestStatus: RequestStatus.LOADING });
    this.reservationService
      .calculate(selecteddata)
      .then((results: ResortPointInterface[]) => {
        this.setState({
          calculatorRequestStatus: RequestStatus.LOADED,
          calculatorRequestData: results,
          selectedCalculatorFilter: selecteddata,
        });
      })
      .catch((error: any) => {
        if (!handle401Error(error)) {
          this.setState({ calculatorRequestStatus: RequestStatus.ERRORED });
          log.error(`Error: ${JSON.stringify(error)}`);
        }
      });
  };

  onAdvancedSearch() {
    let state = this.state;
    state.form.advancedSearch = !state.form.advancedSearch;
    this.setState(state);
  }

  removeItem(propertyCode: string) {
    let results = this.state.searchResults;
    let inx = results
      .map((resort) => {
        return resort.propertyCode;
      })
      .indexOf(propertyCode);
    results.splice(inx, 1);
    this.setState({
      searchResults: results,
    });

    // Remove this resort from the selected resorts in <ResortDropdown />
    this.removeResorts([propertyCode]);
  }

  /**
   * Remove all filters and collapse all filter groups
   */
  clearFilters() {
    this.setState({
      selectedFilters: Object.assign({}, InitSelectedFilters),
    });
  }

  handleInput(name: string, value: any) {
    // let form = JSON.parse(JSON.stringify(this.state.form));
    /**
     * had to remove this property from the deep copy of the form from state before caching, because we are caching based on the md5 conversion of the form from state
     * the page was exhibiting unusual behavior, since advanced search property in state can change and cache will not be invalidated, which will lead to more bugs
     */
    // delete form.advancedSearch;
    // sessionStorage.remove(md5(JSON.stringify(form)));

    let state = this.state;
    let current = state.form;
    current[name] = value;
    // if (name === "flexibleSearch") {
    //     current["startDate"] = "";
    // }
    this.setState({
      form: current,
    });
  }

  handleCalculatorInput = (name: string, value: any) => {
    let state = this.state;
    let current = state.calculatorForm;
    current[name] = value;
    this.setState({
      calculatorForm: current,
    });
  };

  showAvailabilitySection(
    propertyCode: any,
    selectedFiltersMethod: SelectedFilters,
    waitlistToggleChecked: boolean
  ) {
    let availabilty = this.state.availability;
    availabilty.result = propertyCode;
    this.setState({
      showResults: false,
      showAvailability: true,
      availability: availabilty,
      selectedFilters: selectedFiltersMethod,
      waitlistToggleChecked,
    });
  }

  hideAvailabilitySection(firstEnabledDate: any) {
    let newAvailability = this.state.availability;
    newAvailability.result = null;
    // Deep copy form
    let newForm = JSON.parse(JSON.stringify(this.state.form));

    let isDateChanged =
      new Date(this.state.form.startDate).getTime() !==
      new Date(firstEnabledDate).getTime();

    if (isDateChanged) {
      // Update form only when date changed
      if (
        firstEnabledDate &&
        firstEnabledDate !== "" &&
        firstEnabledDate !== undefined
      ) {
        newForm.startDate = moment(firstEnabledDate).format("MM/DD/YYYY");
      }
    } else {
      // Read original form from session
      const oForm = sessionStorage.read(
        ReservationService.OPS_FORM_SESSION_KEY
      );
      if (oForm) {
        newForm = JSON.parse(oForm);
      }
    }

    // handle view filter while coming back to the Search Results
    let newSelectedFilters = this.state.selectedFilters;
    newSelectedFilters.views.filters = []; 
    newSelectedFilters.views.expanded = false;

    this.setState({
      showAvailability: false,
      isDateChanged: isDateChanged,
      hideLoader: false,
      availability: newAvailability,
      selectedMonth: null,
      form: newForm,
      selectedFilters: newSelectedFilters
    },
      () => {
        this.onSubmit();
      }
    );
  }

  arrangeSortResults(sortSearchResults: string) {
    let searchForm = this.state.form;
    searchForm.sortBy = sortSearchResults;
    this.setState({ hideLoader: false, form: searchForm });
    this.onSubmit();
  }

  /**
   * Add or remove a filter from the filter type's array
   */
  onFilterSelect(name: string, item: any) {
    let selectedFilters: SelectedFilters = { ...this.state.selectedFilters };
    const selectedFilter = { ...selectedFilters[name] };
    const idx = selectedFilter.filters.indexOf(item);

    if (idx > -1) {
      selectedFilter.filters = selectedFilter.filters.filter(
        (filterOption: string) => filterOption !== item
      );
    } else {
      selectedFilter.filters = [...selectedFilter.filters, item];

      // If filter group isn't expanded, expand.
      // User could be changing filter from room-type pills.
      if (!selectedFilter.expanded) {
        selectedFilter.expanded = true;
      }
    }
    selectedFilters[name] = selectedFilter;

    this.setState({ selectedFilters });
  }

  /**
   * Toggle the state of a filter group to be expanded or collapsed
   *
   * You can explicitly pass a value to {expand}
   */
  toggleFilterGroup(name: string, expand?: boolean) {
    let selectedFilters: SelectedFilters = { ...this.state.selectedFilters };
    const selectedFilter = { ...selectedFilters[name] };

    // If explicit boolean value is passed, set to that, else toggle
    selectedFilter.expanded =
      expand !== undefined ? expand : !selectedFilter.expanded;

    selectedFilters[name] = selectedFilter;

    this.setState({ selectedFilters });
  }

  /**
   * Get the Temporarily Held Reservation(s)
   */
  getHeldResorts(displayHeldResorts: boolean = true) {
    this.setState({ heldResortsShowLoader: true, displayHeldResorts });
    this.reservationService
      .getHeldResorts()
      .then((results: ResortResult[]) => {
        this.setState({
          heldResorts: results,
          heldResortsShowLoader: false,
        });
      })
      .catch((error: any) => this.catchHeldResortsError(error));
  }

  /**
   * Delete 1 or all of the Temporarily Held Reservation(s).
   *   To delete ALL, pass null.
   *   To delete ONE, pass the ReservedInventoryID.
   */
  deleteHeldResorts(
    ReservedInventoryID: number | null = null,
    refreshHeldResorts: boolean = true
  ) {
    return new Promise((resolve, reject) => {
      this.setState({ heldResortsShowLoader: true });
      this.reservationService
        .deleteHeldResorts(ReservedInventoryID)
        .then(() => {
          refreshHeldResorts && this.getHeldResorts();
          resolve();
        })
        .catch((error: any) => {
          this.catchHeldResortsError(error);
          reject(error);
        });
      this.setState({
        heldResortsShowLoader: false,
      });
    });
  }

  /**
   * Fired when the Select & Continue button is clicked
   */
  confirmHeldResort(reservation: ResortResult): Promise<boolean> {
    sessionStorage.remove(ReservationService.APPLIED_POINTS);
    sessionStorage.remove(ReservationService.BOOKED_RESERVATION);
    sessionStorage.remove(ReservationService.CONTRACT_TOOL_TIPS);
    sessionStorage.remove(ReservationService.SELECTED_PREFERENCES);

    return new Promise<boolean>((resolve, reject) => {
      this.reservationService
        .getHeldResorts()
        .then((holdResults) => {
          const holdResult = holdResults.find(
            (x) => x.ReservedInventoryID == reservation.ReservedInventoryID
          );
          if (holdResult) {
            resolve(true);
            const resId = holdResult.ReservedInventoryID;
            this.goToAYP(resId);
          } else {
            reject(false);
          }
        })
        .catch((error: RequestError) => {
          reject(error);
        });
    });
  }

  deleteOtherHeldResorts(ReservedInventoryID: number) {
    return new Promise((resolve, reject) => {
      // get the current list of held resorts
      this.reservationService
        .getHeldResorts()
        .then((results: ResortResult[]) => {
          const deleteList = results.filter(
            (resort) => ReservedInventoryID !== resort.ReservedInventoryID
          );
          deleteList.map((resort) => {
            // delete all other held resorts
            this.deleteHeldResorts(resort.ReservedInventoryID, false).then(
              (deleteResult) => {}
            );
          });
          resolve();
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  catchHeldResortsError(error: any) {
    const { localize } = this.context;
    // todo: localize the content
    // const errorMessage = error.message ? `${error.message}` : "Unspecified error";
    const errorMessage = error.message
      ? `${error.message}`
      : localize("static.content.pwas-search-fatal-error-message");

    if (!handle401Error(error)) {
      this.setState({
        heldResortsError: true,
        heldResortsErrorMessage: errorMessage,
        heldResortsShowLoader: false,
      });
      log.error(`Error: ${JSON.stringify(error)}`);
    }
  }
  /**
   * Gets and stores the contract tooltip data for all contracts
   */
  fetchContactTooltips = (contractTypes: ContractType[]) => {
    this.setState({
      contractToolTips: [],
    });
    return new Promise((resolve, reject) => {
      contractTypes.map((contractType) => {
        // get a unique list of contract Ids
        const contractList: any = new Set(
          contractType.contracts.map((contract) => contract.id)
        );

        // get the contracts' data
        this.reservationService
          .getOwnerContracts(contractList)
          .then((ownerContracts: OwnerContract[]) => {
            // build the ContractToolTip[]
            let contractToolTips: ContractToolTip[] =
              this.state.contractToolTips || [];
            ownerContracts.map((contract) => {
              // convert OwnerContract to ContractToolTip
              let tempContractToolTip = this.convertOCtoCTT(contract);
              // get the tooltip jsx
              tempContractToolTip.toolTip =
                this.getContractToolTipJSX(tempContractToolTip);
              contractToolTips.push(tempContractToolTip);
            });

            if (this._isMounted) {
              this.setState({
                contractToolTips,
              });
            }
            resolve();
          })
          .catch((error: RequestError) => {
            reject(error);
          });
      });
    });
  };

  /**
   * Converts the contract info into a ContracToolTip
   */
  getContractToolTipJSX(contractToolTip: ContractToolTip): any {
    const contractId = contractToolTip.contractId;
    const key =
      contractId +
      contractToolTip.startDate +
      contractToolTip.endDate +
      (contractToolTip.points ? contractToolTip.points.toString() : "");
    const contractIdStyle = (
      <p
        key={key}
        className="contract-id mv-print-link"
        aria-label={contractId}
      >
        {contractId}
      </p>
    );

    return (
      <MvContractToolTip contract={contractToolTip} position={Position.RIGHT}>
        {contractIdStyle}
      </MvContractToolTip>
    );
  }

  convertOCtoCTT = (oc: OwnerContract): ContractToolTip => {
    // the AYP table is still returning mock data? that will be returning real
    //  data this sprint by Rama - done by tomorrow perhaps
    // getting CORS error
    return {
      id: oc.id,
      contractId: oc.contractNumber,
      contractType: oc.contractType,
      // hard code to points for now
      // the service is not providing this field
      // it is also not returning any weeks data
      typeName: "points",
      startDate: oc.contractStartDate,
      endDate: oc.contractEndDate,
      // this field is missing from the service
      // it is being looked into
      // so for now, just show the start date's year
      usageYear: oc.contractStartDate,
      points: oc.contractPoints,
      ownershipDetails: oc.ownershipDetails
        ? oc.ownershipDetails
        : ({} as OwnerShipDetails), //CE2RT1C-899 - 4. include ownership details when constructing contract tooltip
    };
  };

  onMonthSelect(date: any) {
    this.setState({ selectedMonth: date });
  }

  // this function will be executed when the search form component getting render
  // and check on session storgate key which store the form values
  // to pass these values to the form to avoid lossing form data when refresh the page
  componentWillMount(): void {
    if (sessionStorage.read(ReservationService.OPS_FORM_SESSION_KEY)) {
      let formData = JSON.parse(
        sessionStorage.read(ReservationService.OPS_FORM_SESSION_KEY)
      );
      this.setState({ form: formData });
    }
  }

  render() {
    let content;
    let showLoader = !this.state.hideLoader;
    const { localize } = this.context;
    const isMounted = this._isMounted;
    //let formData = JSON.parse(sessionStorage.read(ReservationService.OPS_FORM_SESSION_KEY)||{})

    console.log("this.state.heldResorts: ", this.state.heldResorts);

    // If displaying <SearchResults/>, show all filters
    // But, if displaying <CheckAvailability/>, only show room-type
    // filters that are available for the resort
    let filters = this.state.filters;
    if (this.state.availability.result) {
      filters["roomType"] = filters["roomType"].filter((room: Item) =>
        this.state.availability.result.available.some(
          (availableRoom: AvailableResult) =>
            availableRoom.roomType === room.value
        )
      );
    }

    if (!this.state.availability.result && this.state.availability.form) {
      // display results
      content = (
        <SearchResults
          onRemoveItem={this.removeItem}
          results={this.state.searchResults}
          searchedID={this.state.form.resorts}
          filters={this.state.filters}
          showAvailability={this.showAvailabilitySection}
          selectedFilters={this.state.selectedFilters}
          toggleFilterGroup={this.toggleFilterGroup}
          arrangeResults={this.arrangeSortResults}
          onFilterSelect={this.onFilterSelect}
          clearFilters={this.clearFilters}
          pointsSearch={this.props.pointsSearch}
          forminputt={this.state.form}
          constructionMsgdata={this.state.constructionMsgData}
          onConstructionMsg={this.handleConstructionMsg}
          // filterData={formData}
          isAlternateSearchAvailable={this.state.isAlternateSearchAvailable}
        />
      );
    } else if (
      this.state.availability.result &&
      this.state.availability.form &&
      isMounted
    ) {
      // Display the availability
      content = (
        <CheckAvailability
          form={this.state.form}
          isAlternateSearchAvailable={this.state.isAlternateSearchAvailable}
          result={this.state.availability}
          onClearSearch={this.clearSearch}
          criteria={this.state["searchCriteria"]}
          filters={this.state.filters}
          hideAvailability={this.hideAvailabilitySection}
          selectedFilters={this.state.selectedFilters}
          toggleFilterGroup={this.toggleFilterGroup}
          onFilterSelect={this.onFilterSelect}
          accessibility={this.state.searchCriteria.accessibility}
          searchMonths={this.state["searchCriteria"].searchMonths}
          clearFilters={this.clearFilters}
          deleteHeldResorts={this.deleteHeldResorts}
          getHeldResorts={this.getHeldResorts}
          placeHold={this.placeHold}
          createWaitlist={this.createWaitlist}
          goToAYP={this.goToAYP}
          goToWaitlistAYP={this.goToWaitlistAYP}
          setEditingSegment={this.props.setEditingSegment}
          editingSegment={this.state.editingSegment}
          toggleAlertModal={this.toggleAlertModal}
          waitlistToggleChecked={this.state.waitlistToggleChecked}
          contracts={this.state.searchCriteria.contracts}
          onMonthSelect={this.onMonthSelect}
          selectedMonth={this.state.selectedMonth}
          sessionId={this.state.searchCriteria.sessionId}
          apiTimeout={this.props.apiTimeout}
        />
      );
    }
    return (
      <main className="bmh-page">
        <MvErrorModal
          data={this.state.alertModal}
          toggle={this.toggleAlertModal}
        />

        <MvPointsCalculatorModal
          localize={localize}
          toggle={this.togglePointsCalculatorModal}
          show={this.state.showPointsCalculatorModal}
          criteria={this.state["searchCriteria"]}
          form={this.state["calculatorForm"]}
          onChange={this.handleCalculatorInput}
          removeResorts={this.removeCalculatorResorts}
          onClearSearch={this.clearCalculatorSearch}
          onSubmit={this.onCalculatorSubmit}
          searchRequestStatus={this.state.calculatorRequestStatus}
          searchResult={this.state.calculatorRequestData}
          selectedCalculatorFilter={this.state.selectedCalculatorFilter}
          apiTimeout={this.props.apiTimeout}
        />
        <MvLoader show={!this.state.showSearch}>
          <React.Fragment>
            <div>
              <h1>{localize("static.content.title")}</h1>
            </div>
            <div className="instructions d-flex-row">
              <i>{localize("static.content.asterisk")}</i>
              {/* Commented this code since Points Calculator is not in Phase 1 */}
              {/* <button type="button" className="mv-button primary" tabIndex={2} id="points-cal" onClick={this.togglePointsCalculatorModal}>Points Calculator</button> */}
            </div>

            <OmniPresentSearchForm
              criteria={this.state["searchCriteria"]}
              form={this.state["form"]}
              isDateChanged={this.state.isDateChanged}
              removeResorts={this.removeResorts}
              onChange={this.handleInput}
              onClearSearch={this.clearSearch}
              onAdvancedSearch={this.onAdvancedSearch}
              onSubmit={this.onSubmit}
              isFormDefault={this.isFormDefault()}
              contractToolTips={this.state.contractToolTips}
              resortLimit={this.props.resortLimit}
              isLoading={!this.state.hideLoader}
            />
            {this.state.loadError && (
              <MvError
                message={localize(
                  "static.content.pwas-search-fatal-error-message"
                )}
              />
            )}
          </React.Fragment>
        </MvLoader>

        <div className="search-loader-wrapper">
          <MvLoader
            show={showLoader}
            loadingMessage={
              this.state.changeLoaderMessage ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: localize("static.content.loading-results-message"),
                  }}
                />
              ) : (
                localize(
                  "static.content.pwas-all-search-results-loading-message"
                )
              )
            }
          >
            {!this.state.resultsError ? (
              <React.Fragment>{content}</React.Fragment>
            ) : (
              <MvError message={this.state.searchResultsErrorMessage} />
            )}
          </MvLoader>
        </div>

        <MvLoader show={this.state.heldResortsShowLoader}>
          {!this.state.heldResortsError ? (
            this.state.heldResorts &&
            this.state.heldResorts.length > 0 && (
              <HeldReservations
                resorts={this.state.heldResorts}
                deleteHeldResorts={this.deleteHeldResorts}
                confirmHeldResort={this.confirmHeldResort}
              />
            )
          ) : (
            <MvError message={this.state.heldResortsErrorMessage} />
          )}
        </MvLoader>
      </main>
    );
  }
}

BookMyHoliday.contextType = LocalizationContext;
export default BookMyHoliday;
